import { useEffect, useState } from 'preact/hooks';

/**
 * 129: true once `navigator.serviceWorker` fires `controllerchange` — the signal that a new
 * worker has taken control of this tab. `service-worker.ts` (123) calls `self.skipWaiting()`
 * unconditionally, so this fires promptly on an open tab without any polling here.
 *
 * 138 (research.md R1): `controllerchange` alone was NOT that signal, and the difference was
 * customer-visible. `service-worker.ts:19-20` calls `skipWaiting()` **and** `clientsClaim()`
 * unconditionally, so the very FIRST worker a device ever installs claims the page that installed
 * it — firing exactly the same event a genuine update fires. Every new visitor, new device,
 * cleared-storage user and private window was told "A new version is available" on their first
 * ever page load. Measured on staging with all workers unregistered and all caches cleared: the
 * banner rendered while the browser reported no waiting and no installing worker.
 *
 * The discriminator is whether a controller was ALREADY running this page when we mounted:
 *
 *   - first load, worker installs and claims → no controller at mount → not an update
 *   - return visit, newer worker takes over  → controller at mount    → an update
 *
 * 🔴 The sample must be taken here, at mount. Reading `.controller` when the event fires answers
 * "yes" in both cases — by then the new worker is always in control — which is the original bug.
 */
export function useServiceWorkerUpdate(): boolean {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    const container = navigator.serviceWorker;
    if (!container) return;

    const hadControllerAtMount = Boolean(container.controller);

    function handleControllerChange() {
      if (!hadControllerAtMount) return;
      setUpdateAvailable(true);
    }

    container.addEventListener('controllerchange', handleControllerChange);
    return () => container.removeEventListener('controllerchange', handleControllerChange);
  }, []);

  return updateAvailable;
}
